/**
 * ac-registry.ts — Registry global de metadados para IA
 *
 * Componente:  <input id="aluno-form-nome" />     ← só o id, limpo
 * IA faz:      AcRegistry.get("aluno-form-nome")   ← recebe tudo
 *
 * Resultado:   { name: "Nome do Aluno", table: "Aluno", field: "nome",
 *               route: "POST /escolas/:id/alunos", required: true, ... }
 *
 * @internal true
 * @owner architecture
 */
import type { AcFieldDef, AcDomainContract } from './types';

class AcRegistryClass {
  /** Map de id → metadados do campo */
  private fields = new Map<string, AcFieldDef>();

  /** Map de domain → contrato completo */
  private domains = new Map<string, AcDomainContract>();

  /** Registra um contrato de domínio inteiro */
  register(contract: AcDomainContract): void {
    this.domains.set(contract.domain, contract);

    // Monta a rota padrão do domínio (CREATE) para herança
    const defaultRoute = contract.routes.criar
      ? `${contract.routes.criar.method} /${contract.routes.criar.path}`
      : undefined;

    for (const def of Object.values(contract.fields)) {
      if (this.fields.has(def.id)) {
        console.warn(`[AcRegistry] ID duplicado: "${def.id}" (domínio: ${contract.domain})`);
      }
      this.fields.set(def.id, {
        ...def,
        // Injeta rota padrão se o field não tem rota própria
        route: def.route || defaultRoute,
      });
    }
  }

  /** Busca metadados de um campo pelo id */
  get(id: string): AcFieldDef | undefined {
    return this.fields.get(id);
  }

  /** Retorna metadados de todos os IDs registrados presentes no DOM atual */
  readPage(): AcFieldDef[] {
    const elements = document.querySelectorAll('[id]');
    const result: AcFieldDef[] = [];
    elements.forEach(el => {
      const def = this.fields.get(el.id);
      if (def) result.push(def);
    });
    return result;
  }

  /**
   * Retorna contexto resumido da página atual para enviar ao LLM.
   * Formato texto otimizado para tokens.
   */
  readPageForAI(): string {
    const items = this.readPage();
    if (!items.length) return 'Nenhum campo AC registrado nesta página.';

    const lines = items.map(f => {
      let line = `• [${f.id}] ${f.name}: ${f.desc}`;
      if (f.table && f.field) line += ` → ${f.table}.${f.field}`;
      if (f.route) line += ` (${f.route})`;
      if (f.required) line += ' [OBRIGATÓRIO]';
      if (f.mask) line += ` [máscara: ${f.mask}]`;
      if (f.validation) line += ` [validação: ${f.validation}]`;
      return line;
    });
    return `Campos na página atual (${items.length}):\n${lines.join('\n')}`;
  }

  /** Retorna contrato de um domínio */
  getDomain(domain: string): AcDomainContract | undefined {
    return this.domains.get(domain);
  }

  /** Lista todos os domínios registrados */
  listDomains(): string[] {
    return Array.from(this.domains.keys());
  }

  /** Exporta todos os contratos (para MCP Server ou debug) */
  exportAll(): AcDomainContract[] {
    return Array.from(this.domains.values());
  }

  /** Total de campos registrados */
  get size(): number {
    return this.fields.size;
  }
}

/** Instância global única — acessível de qualquer lugar da app */
export const AcRegistry = new AcRegistryClass();

// Expõe no window para acesso via console e Inspector
if (typeof window !== 'undefined') {
  (window as any).AcRegistry = AcRegistry;
}
